import { motion } from "framer-motion";
import {
  Building2,
  Megaphone,
  ShoppingBag,
  Boxes,
  LineChart,
  ArrowUpRight,
  CheckCircle2,
  Star,
} from "lucide-react";

const HIGHLIGHT_SERVICES = [
  {
    key: "b2b",
    title: "B2B Marketing",
    icon: Building2,
    pitch:
      "Our flagship offering — fractional marketing leadership for bootstrapped, Angel-funded and Series-A start-ups & SMEs. We embed as your CMO + execution squad.",
    pillar: "Fractional Marketing",
    pillarBody:
      "A fractional CMO + senior pod that owns strategy, unit economics and team upskilling — without the cost of a full-time hire.",
    bullets: [
      "ABM & Account Intelligence",
      "LinkedIn Ads & LinkedIn Marketing",
      "Demand Generation Engines",
      "Performance Marketing",
      "GEO (Generative Engine Optimization)",
      "Lead Generation & Funnel Management",
      "CRM Setup",
      "WhatsApp Business Workflows",
      "Workflows & Marketing Automation",
      "Affiliate & Partner Marketing",
      "Market Research & New Market Access",
      "Custom App / Workflow Development",
    ],
  },
  {
    key: "b2c",
    title: "B2C Marketing",
    icon: Megaphone,
    pitch:
      "Conversion-engineered campaigns for consumer brands — built on Indian buying behaviour and full-funnel performance economics.",
    pillar: "Conversion-focused Ad Creatives",
    pillarBody:
      "Our edge is in creatives that *convert*. We ship high-velocity, hook-tested ad variants that beat algorithm fatigue and unlock cheaper CPMs.",
    bullets: [
      "Performance Marketing (Meta, Google, YouTube)",
      "Paid Social",
      "Paid Search",
      "Creator & Influencer Marketing",
      "Retention & Lifecycle Marketing",
      "Landing-page CRO",
    ],
  },
];

const SUPPORT_SERVICES = [
  {
    key: "d2c",
    title: "D2C Marketing",
    icon: ShoppingBag,
    body:
      "Launch-to-scale playbook for direct-to-consumer brands. WhatsApp-led retention, conversion-rate optimisation, brand systems and creative engines that move week-on-week.",
    tags: ["CRO", "WhatsApp", "Creatives", "Branding"],
  },
  {
    key: "ecommerce",
    title: "Ecommerce Platform Development",
    icon: Boxes,
    body:
      "Storefronts that load fast and sell faster. We design, build and operate on both Shopify and Amazon SmartBiz — themes, headless, listings, ads and catalog ops in one squad.",
    tags: [
      "Shopify",
      "Headless",
      "Amazon SmartBiz",
      "A+ Listings",
      "Catalog Ops",
    ],
  },
  {
    key: "growth",
    title: "Growth & Analytics",
    icon: LineChart,
    body:
      "Server-side tracking, GA4, CAPI, dashboards and lightweight MMM. Every rupee spent has a clear story and a clear next move.",
    tags: ["GA4", "CAPI", "Looker", "Attribution"],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-yellow-400 font-mono-ui text-xs tracking-[0.22em] uppercase mb-4">
              · What we do
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-2xl">
              One zesty team for every growth lever you need.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base sm:text-lg leading-relaxed">
            Strategy, performance, creative and engineering — pre-wired, so
            you’re not stitching five agencies together to ship a quarter.
          </p>
        </div>

        {/* Spotlight: B2B & B2C */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {HIGHLIGHT_SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: idx * 0.08 }}
                data-testid={`service-card-${s.key}`}
                className="group relative overflow-hidden rounded-3xl p-[1.5px] bg-gradient-to-br from-yellow-400/60 via-orange-500/40 to-white/5"
              >
                <div className="relative rounded-[1.4rem] bg-zinc-950 h-full p-7 sm:p-9">
                  <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-yellow-400/0 group-hover:bg-yellow-400/15 blur-3xl transition-all duration-500" />

                  <div className="relative flex items-start justify-between mb-6">
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-yellow-400/15 border border-yellow-400/30 text-yellow-300">
                      <Icon size={22} />
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-300 text-[11px] font-mono-ui uppercase tracking-[0.18em]">
                      <Star size={11} /> Flagship
                    </div>
                  </div>

                  <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed">
                    {s.pitch}
                  </p>

                  <div className="mt-6 rounded-2xl border border-yellow-400/25 bg-yellow-400/[0.04] p-5">
                    <div className="text-[11px] font-mono-ui uppercase tracking-[0.2em] text-yellow-300">
                      Core value prop
                    </div>
                    <div className="mt-1.5 font-display text-xl sm:text-2xl font-semibold text-zinc-50">
                      {s.pillar}
                    </div>
                    <p className="mt-2 text-sm sm:text-base text-zinc-300 leading-relaxed">
                      {s.pillarBody}
                    </p>
                  </div>

                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2.5 text-zinc-300 text-sm"
                      >
                        <CheckCircle2
                          size={15}
                          className="mt-0.5 text-yellow-400 shrink-0"
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Support cards */}
        <div className="mt-6 lg:mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {SUPPORT_SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/40 p-7 hover:border-yellow-400/40 transition-all"
                data-testid={`service-card-${s.key}`}
              >
                <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-yellow-400/0 group-hover:bg-yellow-400/10 blur-3xl transition-all duration-500" />
                <div className="relative flex items-start justify-between mb-5">
                  <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-yellow-400/10 border border-yellow-400/25 text-yellow-300">
                    <Icon size={20} />
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="text-zinc-500 group-hover:text-yellow-400 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                  />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mb-2">
                  {s.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {s.body}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[11px] text-zinc-300 font-mono-ui"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
